// Please see: https://dev.to/0916dhkim/type-safe-usage-of-react-router-5c44
import { useNavigate, useLocation } from "react-router-dom";
import { Location } from "history";
import { LocationStates } from "../types";

export enum RoutePaths {
  any = "/:any",
  createAccount = "/createAccount",
  dashboard = "/dashboard",
  error404 = "/error",
  login = "/login",
  account = "/account",
  accountAnyScreen = "/account/:screen",
  accountChangePassword = "/account/changePassword",
  accountEditProfile = "/account/editProfile",
  accountVerifyEmail = "/account/verifyEmail",
  forgotPassword = "/forgotPassword",
  registerInvitedUser = "/registerUser/:email",
  resetPassword = "/resetPassword",
  resources = "/resources",
  resourcesAny = "/resources/*",
  resourcesLaunchEmail = "/resources/launchEmail",
  resourcesMarketingMaterial = "/resources/marketingMaterial",
  resourcesMarketingMaterialPostcards = "/resources/marketingMaterial/postcards",
  resourcesMarketingMaterialPosters = "/resources/marketingMaterial/posters",
  resourcesMarketingMaterialBrochures = "/resources/marketingMaterial/brochures",
  resourcesMarketingMaterialStandees = "/resources/marketingMaterial/standees",
  resourcesMarketingMaterialVideo = "/resources/marketingMaterial/video",
  resourcesMarketingMaterialManifestDemo = "/resources/marketingMaterial/manifestDemo",
  resourcesMarketingMaterialManifestCake = "/resources/marketingMaterial/manifestCake",
  resourcesMarketingMaterialAddOns = "/resources/marketingMaterial/addOns",
  resourcesOrders = "/resources/orders",
  resourcesDataSecurity = "/resources/dataSecurity",
  settings = "/settings",
  settingsAnyScreen = "/settings/:screen",
  settingsPlanAdmin = "/settings/planAdmin",
  settingsCompany = "/settings/company",
  settingsLaunch = "/settings/launch",
  settingsParticipant = "/settings/participant",
  settingsPlan = "/settings/plan",
  settingsTeam = "/settings/team",
  setup = "/setup",
  setupCompany = "/setup/company",
  setupCompleted = "/setup/completed",
  setupInviteAdmins = "/setup/inviteAdmins",
  setupNextSteps = "/setup/nextSteps",
  setupPlan = "/setup/plan",
  setupLaunch = "/setup/launch",
  support = "/support",
  welcome = "/",
  passwordChanged = "/passwordChanged",
  verifyEmail = "/verifyEmail",
  verifyUserFromEmail = "/verify/:email",
  changelog = "/changelog",
}

type ExtractRouteParams<T> = string extends T
  ? Record<string, string>
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer _Start}:${infer Param}/${infer Rest}`
  ? { [k in Param | keyof ExtractRouteParams<Rest>]: string }
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer _Start}:${infer Param}`
  ? { [k in Param]: string }
  : undefined;
export type PathParams<P extends RoutePaths> = ExtractRouteParams<P>;

export const useTypedLocation = useLocation as () => Location & {
  state: LocationStates;
};
export const useTypedNavigate = useNavigate as () => {
  (
    to: RoutePaths,
    options?: {
      replace?: boolean;
      state?: LocationStates;
    }
  ): void;
  (delta: number): void;
};

/**
 * Build a url with a path and its parameters.
 * @example
 * buildUrl(
 *   '/a/:first/:last',
 *   { first: 'p', last: 'q' },
 * ) // returns '/a/p/q'
 * @param path target path.
 * @param params parameters.
 */
export const route = <P extends RoutePaths>(
  path: P,
  ...params: PathParams<P> extends Record<string, string> ? [PathParams<P> | false] : [undefined?]
) => {
  if ((params as any) === false) return path;
  let ret = `${path}`;
  const paramObj: { [i: string]: string } = (params[0] || {}) as any;
  Object.keys(paramObj).forEach((key) => {
    ret = ret.replace(`:${key}`, paramObj[key]);
  });
  return ret as RoutePaths;
};
